import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/utils'

import {
  markdownToPlainText,
  markdownToPlainTextWithMentions,
} from '../../utils/slateUtils'

interface ExpandableCommentTextProps {
  content: string
  isBold?: boolean
  className?: string
  isAllowExpandable?: boolean
  measureWithMentions?: boolean
}

export const ExpandableCommentText: FC<ExpandableCommentTextProps> = ({
  content,
  isBold = false,
  className,
  isAllowExpandable = true,
  measureWithMentions = false,
}) => {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)
  const [needsExpansion, setNeedsExpansion] = useState(false)
  const collapsedRef = useRef<HTMLDivElement>(null)
  const fullContentRef = useRef<HTMLDivElement>(null)

  const contentElements = useMemo(() => {
    return markdownToPlainTextWithMentions(content)
  }, [content])

  // For height calculation, we still need plain text
  const contentText = useMemo(() => {
    return markdownToPlainText(content)
  }, [content])

  useEffect(() => {
    if (!isAllowExpandable) return

    // Check if content needs expansion by comparing scroll heights
    const checkExpansion = () => {
      if (collapsedRef.current && fullContentRef.current) {
        // Sync width of measurement div with collapsed div
        const collapsedWidth = collapsedRef.current.offsetWidth
        if (collapsedWidth > 0) {
          fullContentRef.current.style.width = `${collapsedWidth}px`
        }

        // Use clientHeight for collapsed (visible height) and scrollHeight for full content
        // line-clamp-2 visually clips but scrollHeight still returns full height
        const collapsedHeight = collapsedRef.current.clientHeight
        const fullHeight = fullContentRef.current.scrollHeight

        // If full content height is greater than collapsed visible height (2 lines), show expand button
        // Add a small threshold (2px) to account for rounding differences
        const shouldExpand = fullHeight > collapsedHeight + 2
        setNeedsExpansion(shouldExpand)
      }
    }

    // Use requestAnimationFrame to ensure DOM is ready
    const rafId = requestAnimationFrame(() => {
      setTimeout(checkExpansion, 0)
    })

    // Also check on window resize
    window.addEventListener('resize', checkExpansion)

    return () => {
      cancelAnimationFrame(rafId)
      window.removeEventListener('resize', checkExpansion)
    }
  }, [contentText, contentElements, measureWithMentions, isAllowExpandable])

  const toggleExpanded = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    setIsExpanded(!isExpanded)
  }

  return (
    <div className={cn('w-full', className)}>
      {/* Hidden measurement div to get full content height - positioned off-screen */}
      <div
        ref={fullContentRef}
        className={cn(
          'w-full text-primary-900 text-sm text-left',
          isBold ? 'font-extrabold' : 'font-normal'
        )}
        style={{
          position: 'absolute',
          visibility: 'hidden',
          height: 'auto',
          top: '-9999px',
          left: '-9999px',
        }}
      >
        {measureWithMentions ? contentElements : contentText}
      </div>

      {/* Collapsed view (2 lines) */}
      {!isExpanded && (
        <div
          ref={collapsedRef}
          className={cn(
            'w-full text-primary-900 text-sm text-left text-ellipsis overflow-hidden line-clamp-2 break-words',
            isBold ? 'font-extrabold' : 'font-normal'
          )}
        >
          {contentElements}
        </div>
      )}

      {/* Expanded view (full content) */}
      {isExpanded && (
        <div
          className={cn(
            'w-full text-primary-900 text-sm text-left break-words whitespace-pre-wrap',
            isBold ? 'font-extrabold' : 'font-normal'
          )}
        >
          {contentElements}
        </div>
      )}

      {/* See more/less button */}
      {needsExpansion && isAllowExpandable && (
        <button
          type="button"
          onClick={toggleExpanded}
          className={cn(
            'text-sm text-brand-tertiary-600 font-medium',
            'hover:text-brand-tertiary-700 transition-colors',
            'border-none bg-transparent p-0 cursor-pointer',
            'mt-1 block'
          )}
          aria-label={isExpanded ? t('common.seeLess') : t('common.seeMore')}
        >
          {isExpanded ? t('common.seeLess') : t('common.seeMore')}
        </button>
      )}
    </div>
  )
}
